<script lang="ts">
  import { createEventDispatcher, onMount, getContext } from 'svelte'
  import { loadStripe } from '@stripe/stripe-js'
  import _ from 'lodash'
  import { setupStripePaymentMethod, verifyStripePaymentMethod, changePaymentBillingAddress } from '../../api'
  import type { PaymentMethod } from '../../api'
  import { profileContextKey } from '../context'
  import { Buttons, CardBrand, FormField, Select } from '../../elements'
  import { normalizeUserCountry, normalizeState, stateList } from '../../helpers'
  import { defaultPaymentMethod } from '../store'

  let stripe
  let card
  let cardReady
  let cardError
  let cardCompleted
  let submitting
  let form
  let cardFocused = false
  let cardChanged = false
  let errorContent

  const dispatch = createEventDispatcher()

  const profileContext = getContext(profileContextKey)
  const userId = profileContext.getUserId()
  const profile = profileContext.getProfile()

  function handleCardFocus() {
    cardFocused = true
  }

  function handleCardBlur() {
    cardFocused = false
  }

  function handleSaved() {
    dispatch('saved')
  }

  function handleCancel() {
    dispatch('cancel')
  }

  async function handleConfirm() {
    const state =
      form.state && form.state.value ? form.state.value : $defaultPaymentMethod?.billing_details.address.state || ''

    errorContent = null
    const billing_details = {
      address: {
        city: form.city.value,
        line1: form.line1.value,
        line2: form.line2.value,
        postal_code: form.postal_code.value,
        country: normalizeUserCountry(form.country.value),
        state,
      },
    } as PaymentMethod['billing_details']
    if (form.name.value) {
      billing_details.name = form.name.value
    }
    if (form.phone.value) {
      billing_details.phone = form.phone.value
    }
    if (form.email.value) {
      billing_details.email = form.email.value
    }

    submitting = true

    if (showStripeElem) {
      const {
        data: {
          setupPaymentMethod: {
            data: { id: setupIntentId, client_secret },
          },
        },
      } = await setupStripePaymentMethod(userId)

      const confirmation = await stripe.confirmCardSetup(client_secret, {
        payment_method: {
          card,
          billing_details,
        },
      })
      if (confirmation.error) {
        const { error } = confirmation
        cardError = error.message
      } else {
        try {
          await verifyStripePaymentMethod(userId, setupIntentId)
          handleSaved()
        } catch (error) {
          const networkError = _.result(error, 'networkError.result.error')
          if (networkError && networkError.code === 14003) {
            cardError = networkError.message
          }
        }
      }
    } else {
      try {
        await changePaymentBillingAddress(userId, $defaultPaymentMethod.id, billing_details)
        handleSaved()
      } catch (error) {
        const networkError = _.result(error, 'networkError.result.error')
        if (networkError && networkError.message) {
          errorContent = networkError.message
        } else {
          throw error
        }
      }
    }

    submitting = false
  }

  async function initStripe() {
    const style = {
      base: {
        color: '#32325d',
        fontSize: '16px',
        lineHeight: '22px',
        '::placeholder': {
          color: '#999',
        },
      },
      invalid: {
        color: '#d83b41',
        iconColor: '#d83b41',
      },
    }

    stripe = await loadStripe('process.env.STRIPE_PUBLISHABLE_KEY')
    const elements = stripe.elements()
    card = elements.create('card', {
      style,
      hidePostalCode: true,
    })

    card.on('ready', () => {
      cardReady = true
    })
    card.on('change', ({ error, complete, empty }) => {
      cardChanged = !empty
      cardCompleted = complete
      cardError = error ? error.message : ''
    })
    card.on('focus', handleCardFocus)
    card.on('blur', handleCardBlur)

    card.mount('#card-element')
  }

  onMount(() => {
    initStripe()
  })

  const address = profile.address || {}
  $: defaultValues = $defaultPaymentMethod?.billing_details
    ? {
        name: $defaultPaymentMethod.billing_details.name,
        email: $defaultPaymentMethod.billing_details.email,
        phone: $defaultPaymentMethod.billing_details.phone,
        ...$defaultPaymentMethod.billing_details.address,
      }
    : {
        name: `${profile.givenName} ${profile.familyName}`,
        email: profile.email,
        phone: profile.phoneHome,
        country: profile.country,
        city: profile.city,
        line1: address.addressLine1,
        line2: address.addressLine2,
        postal_code: address.postalCode,
        state: normalizeState(profile.province),
      }

  $: showStripeElem = !$defaultPaymentMethod || cardChanged || cardFocused
</script>

<svelte:body on:click={handleCardBlur} />

{#if !cardReady}
  <p class="container">Loading Stripe...</p>
{/if}

<div class:hidden={!cardReady} data-cy="edit-payment-card">
  <p class="title">New payment method</p>
  <form bind:this={form}>
    {#if !showStripeElem}
      <div class="card-info-label" on:click|stopPropagation={handleCardFocus}>
        Card Number
        <span>*</span>
      </div>
      <div class="card-info" data-test="card-info" on:click|stopPropagation={handleCardFocus}>
        <div class="card">
          <CardBrand brand={$defaultPaymentMethod.card.brand} />
          <span class="number">**** **** **** {$defaultPaymentMethod.card.last4}</span>
        </div>
        <div class="info">
          <div class="expired-date">
            {$defaultPaymentMethod.card.exp_month}
            / {`${$defaultPaymentMethod.card.exp_year}`.slice(2)}
          </div>
          <div class="cvc">***</div>
        </div>
      </div>
    {/if}
    <div class="field" class:error={cardError} class:hidden={!showStripeElem}>
      <label class="label" for="card-element">
        Card Number
        <span>*</span>
      </label>
      <div id="card-element" class="input" />
      {#if cardError}
        <div class="error" role="alert">{cardError}</div>
      {/if}
    </div>

    <FormField name="name" label="Full name" value={defaultValues.name} />
    <FormField name="line1" label="Street" value={defaultValues.line1} />
    <FormField name="line2" label="Street line 2 (optional)" value={defaultValues.line2} />
    <FormField name="city" label="City" value={defaultValues.city} />
    <div class="field-group">
      <div class="field-group-item left">
        <div class="field">
          <label class="label" for="state">State</label>
          <Select
            placeholder="Choose a state"
            options={stateList}
            value={form && form.state ? form.state.value : defaultValues.state}
            on:input={({ detail: value }) => (form.state = { value })}
            dataCy="state"
          />
        </div>
      </div>
      <div class="field-group-item right">
        <FormField name="postal_code" label="Zip" value={defaultValues.postal_code} />
      </div>
    </div>
    <FormField name="phone" label="Phone (optional)" value={defaultValues.phone} />
    <input hidden name="country" value={defaultValues.country} />
    <input hidden name="email" value={defaultValues.email} />
  </form>

  <Buttons
    cancel="Back"
    confirm={submitting ? 'Saving...' : 'Save'}
    on:cancel={handleCancel}
    on:confirm={handleConfirm}
    confirmDisabled={(showStripeElem && !cardCompleted) || submitting}
    cancelDisabled={submitting}
  />

  {#if errorContent}
    <div class="error-content">
      Error: {errorContent}
    </div>
  {/if}
</div>

<style>
  .hidden {
    display: none;
  }

  .title {
    margin-bottom: 28px;
    font-size: 20px;
    line-height: 28px;
    font-weight: 700;
  }

  .field {
    width: 100%;
    margin-top: 20px;
  }

  .field .label,
  .card-info-label {
    color: #090a0b;
    margin-bottom: 8px;
    font-size: 14px;
    line-height: 16px;
  }

  .field .label span,
  .card-info-label span {
    color: #d83b41;
  }

  .field .input {
    font-size: 16px;
    line-height: 22px;
    color: #515151;
    border: 1px solid #b1b5b9;
    border-radius: 4px;
    height: 48px;
    box-sizing: border-box;
    padding: 13px 16px;
  }

  .field.error .input {
    border-color: #d83b41;
  }

  .field .error,
  .error-content {
    font-size: 11px;
    line-height: 15px;
    margin-top: 4px;
    color: #d83b41;
  }

  .field-group {
    display: flex;
  }

  .field-group-item {
    flex: 1;
  }

  .field-group-item.left {
    margin-right: 4px;
  }

  .field-group-item.right {
    margin-left: 4px;
  }

  .card-info {
    width: 100%;
    height: 48px;
    border-radius: 4px;
    border: 1px solid #b1b5b9;
    display: flex;
    justify-content: space-between;
  }

  .card-info .card {
    display: flex;
    align-items: center;
    padding-left: 16px;
  }

  .card-info .card .number {
    display: inline-block;
    margin-left: 16px;
  }

  .card-info .info {
    padding-right: 16px;
    display: flex;
    align-items: center;
  }

  .card-info .info .expired-date {
    display: inline-block;
    margin-right: 16px;
  }

  .card-info .info .cvc {
    display: inline-block;
  }
</style>
